// Tracks which listings a buyer has already paid to unlock, in their OWN
// browser's localStorage — so refreshing the page, or coming back later
// (within 24 hours), doesn't force them to pay again.
//
// This is intentionally device-local, not account-based: nothing here is
// sent to or checked against the server beyond the original payment
// verification. It won't follow a buyer to a different phone or a freshly
// cleared browser — that's a deliberate trade-off for keeping this simple
// and not requiring buyer accounts/logins.

export const UNLOCK_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

export const unlockStorageKey = (table: string, id: string) => `unlock-req-${table}-${id}`;

export type StoredUnlock = {
  ref: string;
  method: 'stk' | 'manual';
  verified?: boolean;
  verifiedAt?: number;
  listingTable: string;
  listingId: string;
  listingTitle: string;
};

export const saveVerifiedUnlock = (record: StoredUnlock) => {
  try {
    localStorage.setItem(unlockStorageKey(record.listingTable, record.listingId), JSON.stringify(record));
  } catch {
    // localStorage unavailable (private browsing, quota, etc.) — not critical, just skip persisting.
  }
};

export const isUnlockStillValid = (record: StoredUnlock | null | undefined): boolean => {
  if (!record || !record.verified || !record.verifiedAt) return false;
  return Date.now() - record.verifiedAt < UNLOCK_TTL_MS;
};

// Reads a single listing's stored unlock record, if any (used by the unlock modal).
export const getStoredUnlock = (table: string, id: string): StoredUnlock | null => {
  try {
    const raw = localStorage.getItem(unlockStorageKey(table, id));
    if (!raw) return null;
    return JSON.parse(raw) as StoredUnlock;
  } catch {
    return null;
  }
};

// Scans localStorage for every still-valid unlocked listing (used by the "My Unlocked Listings" page).
// Also quietly cleans up entries that have expired.
export const getAllValidUnlocks = (): StoredUnlock[] => {
  const results: StoredUnlock[] = [];
  try {
    const keys: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith('unlock-req-')) keys.push(key);
    }
    for (const key of keys) {
      try {
        const parsed = JSON.parse(localStorage.getItem(key) || '') as StoredUnlock;
        if (isUnlockStillValid(parsed)) {
          results.push(parsed);
        } else if (parsed?.verified) {
          // Was verified but the 24-hour window has passed — clean it up.
          localStorage.removeItem(key);
        }
      } catch {
        // Skip malformed entries.
      }
    }
  } catch {
    // localStorage unavailable — return whatever we have (nothing).
  }
  return results.sort((a, b) => (b.verifiedAt || 0) - (a.verifiedAt || 0));
};
